import { memo, useCallback, useState } from "react"
import { IconMegaphoneOutline18 as FeedbackIcon } from "nucleo-ui-outline-18"

import { Button, Popover, Textarea } from "@productlane/ui"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import {
	canSubmitFeedback,
	PAGE_FEEDBACK_MAX_LENGTH,
	remainingFeedbackCharacters,
	shouldShowFeedbackCounter,
} from "./reporting-feedback"

export const ReportingFeedbackButton = memo(() => {
	const [isOpen, setIsOpen] = useState(false)
	const [message, setMessage] = useState("")

	const createPageFeedback = api.workspace.createPageFeedback.useMutation()

	const canSubmit = canSubmitFeedback(message) && !createPageFeedback.isLoading

	const handleOpenChange = useCallback((open: boolean) => {
		setIsOpen(open)
		if (!open) setMessage("")
	}, [])

	const handleSubmit = useCallback(async () => {
		if (!canSubmitFeedback(message) || createPageFeedback.isLoading) return

		try {
			await createPageFeedback.mutateAsync({
				page: "reporting",
				message: message.trim(),
			})
			analytics.track("submit-reporting-feedback")
			setMessage("")
			setIsOpen(false)
			toast.success({
				title: "Thanks for the feedback",
				icon: FeedbackIcon,
			})
		} catch (error) {
			captureError(error, {
				domain: "general",
				action: "submit reporting feedback",
			})
			toast.error({
				title: "Your feedback didn't send",
				description: "Try again in a moment.",
				icon: FeedbackIcon,
			})
		}
	}, [createPageFeedback, message])

	return (
		<Popover
			open={isOpen}
			onOpenChange={handleOpenChange}
			align="end"
			className="w-80 p-0"
			content={
				<div>
					<Textarea
						name="reporting-feedback"
						aria-label="What would you like to see on the reporting page?"
						variant="borderless"
						autoFocus
						value={message}
						onChange={(event) => setMessage(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
								event.preventDefault()
								void handleSubmit()
							}
						}}
						maxLength={PAGE_FEEDBACK_MAX_LENGTH}
						minRows={3}
						maxRows={10}
						placeholder="A metric, a breakdown, a chart you keep rebuilding by hand..."
						inputClassName="cursor-text px-3 pt-3 pb-2"
					/>
					<div className="flex items-center justify-between gap-2 border-t border-gray-200 px-3 py-2 dark:border-gray-700/80">
						<span className="text-xs text-tertiary">
							{shouldShowFeedbackCounter(message)
								? `${remainingFeedbackCharacters(message)} characters left`
								: "Goes to our product team"}
						</span>
						<Button
							size="sm"
							disabled={!canSubmit}
							isLoading={createPageFeedback.isLoading}
							onClick={() => void handleSubmit()}
						>
							Send
						</Button>
					</div>
				</div>
			}
		>
			<Button variant="ghost" Icon={FeedbackIcon}>
				<span className="hidden sm:inline">Give feedback</span>
			</Button>
		</Popover>
	)
})
